import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Loader2, Check } from "lucide-react";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import "./UserProfileEditPage.css";
import apiClient from "../../api/apiClient";
import { updateProfileInStore } from "../../redux/userSlice";

import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";

const libraries = ["places"];
const fallbackCenter = { lat: 40.7648, lng: -73.9808 };

const interactiveMapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "auto",
  mapTypeControl: false,
  streetViewControl: false,
};

const UserProfileEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.user);

  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if Social Signup & Team Status
  const isSocialAuth = profile?.is_social_signup === 1;
  const isTeamPaws = profile?.user_dog_status === 1;

  // Mobile Step State (1 for Left Panel, 2 for Right Panel)
  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipcode: "",
    latitude: "",
    longitude: "",
    reasons: [],
    customReason: "",
  });

  // Base Reasons specific to Dog Status
  const baseReasons = isTeamPaws
    ? [
        "My dog is alone more than I would like",
        "My dog could use extra play and stimulation",
        "I want more socialization for my dog",
        "I want to save money on doggy daycare",
        "I want more options for when I travel",
        "I want to meet more people",
      ]
    : [
        "I'm between dogs, and want my pup fix",
        "I'm missing my pup back home",
        "My kids are dog-crazy",
        "I love dogs, and just can't have one right now",
        "I would make a great dog aunt or uncle",
      ];

  const [dynamicReasons, setDynamicReasons] = useState(baseReasons);

  // Map & Location States
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [activeAddressTab, setActiveAddressTab] = useState("pin");
  const [mapCenter, setMapCenter] = useState(fallbackCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [pinnedAddressLabel, setPinnedAddressLabel] = useState("");
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [pinMapRef, setPinMapRef] = useState(null);
  const addressDebounceRef = useRef(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          const u = res.data.data;

          // Re-determine reasons based on fresh API data
          const isFetchedTeamPaws = u.user_dog_status === 1;
          const fetchedBaseReasons = isFetchedTeamPaws
            ? [
                "My dog is alone more than I would like",
                "My dog could use extra play and stimulation",
                "I want more socialization for my dog",
                "I want to save money on doggy daycare",
                "I want more options for when I travel",
                "I want to meet more people",
              ]
            : [
                "I'm between dogs, and want my pup fix",
                "I'm missing my pup back home",
                "My kids are dog-crazy",
                "I love dogs, and just can't have one right now",
                "I would make a great dog aunt or uncle",
              ];

          let fetchedReasons = [];
          if (Array.isArray(u.whats_brings_you)) {
            fetchedReasons = u.whats_brings_you;
          } else if (typeof u.whats_brings_you === "string") {
            try {
              fetchedReasons = JSON.parse(u.whats_brings_you);
            } catch {
              fetchedReasons = [];
            }
          }

          // Sanitize fetched reasons
          fetchedReasons = fetchedReasons.filter(
            (item) =>
              typeof item === "string" &&
              item.trim() !== "" &&
              item.trim() !== "[" &&
              item.trim() !== "]",
          );

          const updatedList = [...fetchedBaseReasons];
          fetchedReasons.forEach((reason) => {
            if (!updatedList.includes(reason)) updatedList.push(reason);
          });
          setDynamicReasons(updatedList);

          setFormData({
            name: u.user_name || "",
            email: u.user_email || "",
            addressLine1: u.user_address || "",
            addressLine2: u.user_address_line_2 || "",
            city: u.user_city || "",
            state: u.user_state || "",
            zipcode: u.user_zipcode || "",
            latitude: u.user_latitude || "",
            longitude: u.user_longitude || "",
            reasons: fetchedReasons,
            customReason: "",
          });

          // Set Initial Map State
          if (u.user_latitude && u.user_longitude) {
            const lat = parseFloat(u.user_latitude);
            const lng = parseFloat(u.user_longitude);
            setMapCenter({ lat, lng });
            setMarkerPosition({ lat, lng });
          }

          const fullAddress =
            `${u.user_address || ""}, ${u.user_city || ""}, ${u.user_state || ""} ${u.user_zipcode || ""}`
              .replace(/^[,\s]+|[,\s]+$/g, "")
              .replace(/,\s*,/g, ",")
              .trim();
          setPinnedAddressLabel(fullAddress);
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  useEffect(() => {
    if (pinMapRef && mapCenter) pinMapRef.panTo(mapCenter);
  }, [pinMapRef, mapCenter]);

  // Handle manual input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomReasonChange = (value) => {
    setFormData((prev) => {
      const currentList = prev.reasons || [];

      const oldTrimmedValue = (prev.customReason || "").trim();
      const newTrimmedValue = value.trim();

      let updatedList = currentList.filter((item) => item !== oldTrimmedValue);

      if (newTrimmedValue !== "") {
        updatedList = [...updatedList, newTrimmedValue];
      }

      return {
        ...prev,
        reasons: updatedList,
        customReason: value,
      };
    });
  };

  const toggleReason = (option) => {
    setFormData((prev) => {
      const current = prev.reasons || [];
      const updated = current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option];
      return { ...prev, reasons: updated };
    });
  };

  // MAP & ADDRESS HANDLERS
  const parseAddressComponents = (components, formattedAddress = "") => {
    let streetNum = "",
      route = "",
      city = "",
      state = "",
      zipcode = "",
      country = "";

    components.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number")) streetNum = component.long_name;
      if (types.includes("route")) route = component.long_name;
      if (
        types.includes("locality") ||
        types.includes("sublocality") ||
        types.includes("administrative_area_level_2")
      ) {
        if (!city) city = component.long_name;
      }
      if (types.includes("administrative_area_level_1"))
        state = component.short_name;
      if (types.includes("postal_code")) zipcode = component.long_name;
      if (types.includes("country")) country = component.long_name;
    });

    let addressLine1 = `${streetNum} ${route}`.trim();
    if (!addressLine1 && formattedAddress) {
      addressLine1 = formattedAddress.split(",")[0].trim();
    }

    return { addressLine1, city, state, zipcode, country };
  };

  const fetchCoordsFromAddress = useCallback(
    (address, shouldAutofill = true) => {
      if (!window.google || !address.trim()) return;

      setIsFetchingLocation(true);
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });

          const fullAddress = results[0].formatted_address;
          setPinnedAddressLabel(fullAddress);

          if (shouldAutofill) {
            const parsedAddress = parseAddressComponents(
              results[0].address_components,
              fullAddress,
            );

            setFormData((prev) => ({
              ...prev,
              latitude: lat,
              longitude: lng,
              addressLine1: parsedAddress.addressLine1 || prev.addressLine1,
              city: parsedAddress.city || prev.city,
              state: parsedAddress.state || prev.state,
              zipcode: parsedAddress.zipcode || prev.zipcode,
            }));
          }
        } else {
          console.error("Geocoder failed: " + status);
        }
        setIsFetchingLocation(false);
      });
    },
    [],
  );

  useEffect(() => {
    if (activeAddressTab !== "manual") return;

    const addressLine1 = formData.addressLine1 || "";
    const city = formData.city || "";
    const state = formData.state || "";

    if (addressLine1.length > 3 && city.length > 2) {
      const fullAddress = `${addressLine1}, ${city}, ${state}`.trim();

      if (addressDebounceRef.current) {
        clearTimeout(addressDebounceRef.current);
      }

      addressDebounceRef.current = setTimeout(() => {
        fetchCoordsFromAddress(fullAddress, false);
      }, 1500);
    }
  }, [
    formData.addressLine1,
    formData.city,
    formData.state,
    activeAddressTab,
    fetchCoordsFromAddress,
  ]);

  const handleGetCurrentLocation = () => {
    if ("geolocation" in navigator) {
      setIsFetchingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });

          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
              const fullAddress = results[0].formatted_address;
              const parsedAddress = parseAddressComponents(
                results[0].address_components,
                fullAddress,
              );

              setFormData((prev) => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                addressLine1: parsedAddress.addressLine1 || prev.addressLine1,
                city: parsedAddress.city || prev.city,
                state: parsedAddress.state || prev.state,
                zipcode: parsedAddress.zipcode || prev.zipcode,
              }));

              setPinnedAddressLabel(fullAddress);
            }
            setIsFetchingLocation(false);
          });
        },
        (error) => {
          console.error("Error getting location", error);
          setIsFetchingLocation(false);
          setMapCenter(fallbackCenter);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 10000 },
      );
    } else {
      alert("Geolocation is not supported by your browser");
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    setMapCenter({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const fullAddress = results[0].formatted_address;
        setPinnedAddressLabel(fullAddress);

        const parsedAddress = parseAddressComponents(
          results[0].address_components,
          fullAddress,
        );
        setFormData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          addressLine1: parsedAddress.addressLine1 || prev.addressLine1,
          city: parsedAddress.city || prev.city,
          state: parsedAddress.state || prev.state,
          zipcode: parsedAddress.zipcode || prev.zipcode,
        }));
      }
    });
  };

  const handleTabSwitch = (tab) => {
    setActiveAddressTab(tab);
    if (tab === "pin" && !pinnedAddressLabel && formData.addressLine1) {
      const combinedAddress =
        `${formData.addressLine1}, ${formData.city}, ${formData.state}`
          .replace(/,\s*,/g, ",")
          .trim();
      setPinnedAddressLabel(combinedAddress);
    }
  };

  const handlePinnedAddressChange = (value) => {
    setPinnedAddressLabel(value);
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }
    if (!value.trim()) return;

    addressDebounceRef.current = setTimeout(() => {
      if (value.trim().length >= 8) {
        fetchCoordsFromAddress(value, true);
      }
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (addressDebounceRef.current) {
        clearTimeout(addressDebounceRef.current);
      }
    };
  }, []);

  const getCoordinatesFromAddress = async (addressString) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          addressString,
        )}&key=${apiKey}`,
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        return { lat, lng };
      }
      return null;
    } catch (error) {
      console.error("Geocoding error:", error);
      return null;
    }
  };

  const handleUpdate = async () => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      let currentLat = formData.latitude;
      let currentLng = formData.longitude;

      // Fallback geocoding if coordinates somehow didn't generate
      if (!currentLat || !currentLng) {
        const fullAddress = `${formData.addressLine1}, ${formData.city}, ${formData.state} ${formData.zipcode}`;
        const coords = await getCoordinatesFromAddress(fullAddress);
        if (coords) {
          currentLat = coords.lat;
          currentLng = coords.lng;
        }
      }

      const payload = {
        user_name: formData.name,
        user_address: formData.addressLine1,
        user_address_line_2: formData.addressLine2,
        user_city: formData.city,
        user_state: formData.state,
        user_zipcode: formData.zipcode,
        user_latitude: currentLat,
        user_longitude: currentLng,
        whats_brings_you: formData.reasons,
      };

      const updateRes = await apiClient.put(
        `/user/update-user-byId/${profile?.id}`,
        payload,
      );

      if (!updateRes.data?.status) throw new Error("Update failed");

      const detailsRes = await apiClient.get(`/user/details/${profile?.id}`);
      if (detailsRes.data?.data) {
        await dispatch(updateProfileInStore(detailsRes.data.data));
      }

      navigate("/edit-user-details-two");
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  // Validation
  const isStep1Valid = () => {
    const { name, email, addressLine1, city, state } = formData;
    return name.trim() && email.trim() && addressLine1.trim() && city.trim();
  };

  const isStep2Valid = () => {
    return (
      formData.reasons.length > 0 || formData.customReason.trim().length > 0
    );
  };

  const isFormValid = () => {
    return isStep1Valid() && isStep2Valid();
  };

  const handleNext = () => {
    if (window.innerWidth >= 1024) {
      if (isFormValid()) handleUpdate();
    } else {
      if (isStep2Valid()) handleUpdate();
    }
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} color="#fff" />
      </div>
    );
  }

  return (
    <div
      className={`upe-main-container ${
        mobileStep === 1 ? "upe-mobile-step-1" : "upe-mobile-step-2"
      }`}
    >
      <div className="upe-split-layout">
        {/* LEFT SECTION: Basic Info */}
        <section
          className={`upe-section upe-left-section ${
            mobileStep === 1 ? "upe-mobile-active" : "upe-mobile-hidden"
          }`}
        >
          <div style={{ margin: "0 -1.5rem" }}>
            <MobileTopBar
              onBack={() => navigate("/select-edit-profile")}
              backColor="#000"
              logoColor="#009fd9"
              blueLogo="true"
            />
          </div>

          <DesktopTopBar
            showBack={true}
            onBack={() => navigate("/select-edit-profile")}
            backColor="#000"
          />

          <h2 className="upe-section-title">PROFILE</h2>

          <div className="upe-input-group">
            <label>WHAT'S YOUR NAME?</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Name"
            />
          </div>

          <div className="upe-input-group">
            <label>EMAIL</label>
            <input
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              disabled
            />
          </div>
          {!isSocialAuth && (
            <div className="upe-input-group">
              <label>PASSWORD</label>
              <button
                className="upe-change-pw-btn"
                onClick={() => {
                  navigate("/profile-change-password", {
                    state: { email: formData.email },
                  });
                }}
              >
                CHANGE PASSWORD
              </button>
            </div>
          )}

          <div className="upe-input-group" style={{ marginBottom: "0" }}>
            <label>ADDRESS</label>

            {/* TABS HEADER */}
            {/* <div className="upe-address-tabs">
              <button
                className={`upe-tab-btn ${
                  activeAddressTab === "manual" ? "active" : ""
                }`}
                onClick={() => handleTabSwitch("manual")}
              >
                Enter your address below
              </button>
              <button
                className={`upe-tab-btn ${
                  activeAddressTab === "pin" ? "active" : ""
                }`}
                onClick={() => handleTabSwitch("pin")}
              >
                Pin Your Location
              </button>
            </div> */}

            {/* TAB 1: MANUAL */}
            {activeAddressTab === "manual" ? (
              <div className="upe-address-container">
                <button
                  type="button"
                  className="upe-get-location-text"
                  onClick={handleGetCurrentLocation}
                  disabled={isFetchingLocation}
                >
                  {isFetchingLocation
                    ? "Fetching location..."
                    : "Get Your Current Location"}
                </button>
                <input
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleInputChange}
                  placeholder="Street address"
                />
                <input
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleInputChange}
                  placeholder="Apt, unit (optional)"
                  className="upe-mt-10"
                />
                <div className="upe-address-row upe-mt-10">
                  <input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                  />
                  <input
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State"
                  />
                  <input
                    name="zipcode"
                    value={formData.zipcode}
                    onChange={handleInputChange}
                    placeholder="ZIP"
                  />
                </div>
              </div>
            ) : (
              /* TAB 2: PIN YOUR LOCATION */
              <div className="upe-address-container">
                <div className="upe-map-pin-view">
                  {!isLoaded ? (
                    <Loader2
                      className="animate-spin"
                      size={32}
                      color="#009fd9"
                    />
                  ) : (
                    <GoogleMap
                      onLoad={(map) => setPinMapRef(map)}
                      mapContainerStyle={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "15px",
                      }}
                      center={mapCenter}
                      zoom={14}
                      options={interactiveMapOptions}
                      onClick={handleMapClick}
                    >
                      {markerPosition && <MarkerF position={markerPosition} />}
                    </GoogleMap>
                  )}
                  {isLoaded && !markerPosition && (
                    <div className="upe-map-instructions">
                      Drag map and click to pin location
                    </div>
                  )}
                </div>

                <div className="upe-pinned-address-display upe-mt-10">
                  <label>Address:</label>
                  {isFetchingLocation ? (
                    <span>Updating address...</span>
                  ) : (
                    <input
                      type="text"
                      value={pinnedAddressLabel}
                      onChange={(e) =>
                        handlePinnedAddressChange(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (addressDebounceRef.current) {
                            clearTimeout(addressDebounceRef.current);
                          }
                          fetchCoordsFromAddress(pinnedAddressLabel, true);
                        }
                      }}
                      placeholder="Type an address or click the map..."
                      className="upe-transparent-input"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <MobileBottomBar
            onNext={() => isStep1Valid() && setMobileStep(2)}
            isNextEnabled={isStep1Valid()}
            nextIconSrc={nextIcon2}
            showSkip={true}
            isSkipBlue={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="blue"
          />
        </section>

        {/* RIGHT SECTION: Reasons Checklist */}
        <section
          className={`upe-section upe-right-section ${
            mobileStep === 2 ? "upe-mobile-active" : "upe-mobile-hidden"
          }`}
        >
          <div style={{ margin: "0 -1.5rem" }}>
            <MobileTopBar
              onBack={() => setMobileStep(1)}
              backColor="#fff"
              logoColor="#fff"
            />
          </div>

          <div className="upe-content-wrapper-right">
            <h2 className="upe-section-title-alt">WHAT BRINGS YOU HERE?</h2>

            <div className="upe-checklist">
              {dynamicReasons.map((option, index) => {
                const isChecked = formData.reasons.includes(option);
                return (
                  <div
                    key={index}
                    className="upe-checklist-item"
                    onClick={() => toggleReason(option)}
                  >
                    <span className="upe-option-text">{option}</span>
                    <div
                      className={`upe-checkbox ${isChecked ? "checked" : ""}`}
                    >
                      {isChecked && (
                        <Check size={16} strokeWidth={4} color="#000" />
                      )}
                    </div>
                  </div>
                );
              })}

              <div
                className="tp-write-own-box tp-bg-light-blue"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingRight: "15px",
                  marginTop: "1.5rem",
                }}
              >
                <input
                  type="text"
                  placeholder="Write your own"
                  className="tp-write-own-input"
                  style={{
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    fontSize: "1.1rem",
                    width: "100%",
                  }}
                  value={formData.customReason}
                  onChange={(e) => handleCustomReasonChange(e.target.value)}
                />
                {formData.customReason?.trim() && (
                  <div
                    className="upe-checkbox checked"
                    style={{
                      border: "none",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginLeft: "6px",
                    }}
                  >
                    <Check size={18} strokeWidth={4} color="#000" />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Desktop Footer */}
          <div className="upe-footer-actions desktop-only">
            <button
              className="upe-exit-btn"
              onClick={() => navigate("/user-profile")}
            >
              EXIT
            </button>
            <div
              className={`upe-next-wrapper ${
                !isFormValid() || isUpdating ? "disabled" : ""
              }`}
              onClick={isFormValid() && !isUpdating ? handleUpdate : undefined}
            >
              <span className="upe-next-text">
                {isUpdating ? "Updating..." : "Next"}
              </span>
              <div className="upe-next-circle">
                {isUpdating ? (
                  <Loader2 className="animate-spin" size={20} color="#fff" />
                ) : (
                  <div className="upe-paw-icon" />
                )}
              </div>
            </div>
          </div>

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isFormValid() && !isUpdating}
            nextIconSrc={nextIcon}
            showSkip={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="green"
          />
        </section>
      </div>
    </div>
  );
};

export default UserProfileEditPage;
